<template>
  <div>
    <!-- Trigger Button -->
    <button
      @click="openModal"
      class="btn-primary"
    >
      Attach Teams Meeting
    </button>

    <!-- Modal -->
    <div v-if="isModalOpen" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <!-- Background overlay -->
        <div class="fixed inset-0 bg-gray-500/75 transition-opacity" @click="closeModal"></div>

        <!-- Modal panel -->
        <div class="relative z-10 inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <!-- Header -->
          <div class="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <div class="flex items-center justify-between">
              <h3 class="text-lg font-medium text-gray-900">Select Teams Meeting</h3>
              <button @click="closeModal" class="text-gray-400 hover:text-gray-500">
                <FontAwesomeIcon :icon="['fas', 'times']" class="h-5 w-5" />
              </button>
            </div>
          </div>

          <!-- Meeting List Component -->
          <TeamsMeetingList
            :meeting-class-function="getMeetingClasses"
            :start-date="startDate"
            :end-date="endDate"
            @meeting-selected="selectMeeting"
          >
            <template #meeting-badge="{ meeting }">
              <div
                v-if="meeting.iCalUId === currentMeetingId"
                class="absolute top-2 right-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full"
              >
                Currently Attached
              </div>
            </template>
          </TeamsMeetingList>

          <!-- Footer -->
          <div class="bg-gray-50 px-6 py-4 border-t border-gray-200">
            <div class="flex justify-between items-center">
              <div v-if="selectedMeeting" class="text-sm text-gray-600">
                Selected: {{ selectedMeeting.subject || 'Untitled Meeting' }}
              </div>
              <div v-else class="text-sm text-gray-500">
                Select a meeting to attach
              </div>
              <div class="flex gap-3">
                <button
                  @click="closeModal"
                  class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
                <button
                  @click="attachMeeting"
                  :disabled="!selectedMeeting || attaching"
                  class="btn-primary-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-dark"
                >
                  <span v-if="attaching" class="flex items-center gap-2">
                    <svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Attaching...
                  </span>
                  <span v-else>Attach Meeting</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import TeamsMeetingList from './TeamsMeetingList.vue';
import {mapGetters} from "vuex";

export default defineComponent({
  name: 'TeamsMeetingSelector',
  components: {
    FontAwesomeIcon,
    TeamsMeetingList
  },
  props: {
    engagementId: {
      type: [String, Number],
      required: true
    },
    engagementDate:{
      type: String,
      required: false,
      default: null
    },
    currentMeetingId: {
      type: String,
      required: false,
      default: null
    }
  },
  data() {
    return {
      isModalOpen: false,
      selectedMeeting: null,
      attaching: false
    };
  },
  computed : {
    // Computed property to get the current user's ID
    ...mapGetters('auth', ['isAuthenticated', 'current_user']),
    startDate() {
      const d = new Date(this.engagementDate);
      d.setDate(d.getDate() - 1);
      return d.toISOString().split('T')[0];
    },

    endDate() {
      const d = new Date(this.engagementDate);
      d.setDate(d.getDate() + 1);
      return d.toISOString().split('T')[0];
    }
  },
  methods: {
    openModal() {
      this.isModalOpen = true;
    },

    closeModal() {
      this.isModalOpen = false;
      this.selectedMeeting = null;
    },

    getMeetingClasses(meeting) {
      if (meeting.iCalUId === this.currentMeetingId) {
        return 'border-green-500 bg-green-50';
      }
      if (this.selectedMeeting?.id === meeting.id) {
        return 'border-orange-500 bg-orange-50';
      }
      return '';
    },

    selectMeeting(meeting) {
      if (meeting.isCancelled) {
        return;
      }
      this.selectedMeeting = meeting;
    },

    async attachMeeting() {
      if (!this.selectedMeeting) return;

      this.attaching = true;

      try {
        // Prepare the meeting data for the API
        const payload = {
          meeting_id: this.selectedMeeting.id,
          ical_uid: this.selectedMeeting.iCalUId,
          join_url: this.selectedMeeting.onlineMeeting?.joinUrl,
          subject: this.selectedMeeting.subject,
          start_datetime: this.selectedMeeting.start?.dateTime,
          online_meeting_id: this.selectedMeeting.onlineMeetingId || null,
          fetch_transcript: this.selectedMeeting.hasTranscript === true
        };

        // Call the attach meeting endpoint
        const response = await axios.post(
          `/api/engagements/${this.engagementId}/attach-teams-meeting/`,
          payload
        );

        if (response.data.success) {
          // Emit success event with response data
          this.$emit('meeting-attached', {
            meeting: this.selectedMeeting,
            attachmentResult: response.data
          });

          this.closeModal();
        } else {
          // Handle error
          console.error('Failed to attach meeting:', response.data.error);
        }

      } catch (error) {
        console.error('Error attaching meeting:', error);
      } finally {
        this.attaching = false;
      }
    }
  }
});
</script>
